import React, { useState, useEffect } from 'react';
import {
  Settings,
  Key,
  User,
  Shield,
  Eye,
  EyeOff,
  Check,
  AlertTriangle,
  Building2
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';
import datiAziendaService, { DatiAzienda } from '@/services/datiAziendaService';
import styles from './ImpostazioniPage.module.css';

const DATI_AZIENDA_VUOTI: DatiAzienda = {
  ragioneSociale: '',
  indirizzo: '',
  cap: '',
  citta: '',
  provincia: '',
  partitaIva: '',
  codiceFiscale: '',
  telefono: '',
  email: '',
  sitoWeb: '',
  noteDocumento: '',
};

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface PasswordFormErrors {
  currentPassword?: boolean;
  newPassword?: boolean;
  confirmPassword?: boolean;
  message?: string;
}

export const ImpostazioniPage: React.FC = () => {
  const { user } = useAuth();

  // Password change form
  const [passwordData, setPasswordData] = useState<PasswordFormData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState<PasswordFormErrors>({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Dati azienda: intestazione dei documenti d'ordine stampati
  const [datiAzienda, setDatiAzienda] = useState<DatiAzienda>(DATI_AZIENDA_VUOTI);
  const [aziendaLoading, setAziendaLoading] = useState(true);
  const [aziendaSaving, setAziendaSaving] = useState(false);
  const [aziendaError, setAziendaError] = useState<string | null>(null);
  const [aziendaSuccess, setAziendaSuccess] = useState<string | null>(null);

  useEffect(() => {
    const loadDatiAzienda = async () => {
      try {
        const dati = await datiAziendaService.getDatiAzienda();
        if (dati) {
          setDatiAzienda({ ...DATI_AZIENDA_VUOTI, ...dati });
        }
      } catch (error) {
        console.error('Errore nel caricamento dei dati azienda:', error);
      } finally {
        setAziendaLoading(false);
      }
    };
    loadDatiAzienda();
  }, []);

  const handleAziendaChange = (field: keyof DatiAzienda, value: string) => {
    setDatiAzienda(prev => ({ ...prev, [field]: value }));
    setAziendaError(null);
    setAziendaSuccess(null);
  };

  const handleSubmitAzienda = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!datiAzienda.ragioneSociale.trim()) {
      setAziendaError('La ragione sociale è obbligatoria');
      return;
    }

    setAziendaSaving(true);
    try {
      const salvati = await datiAziendaService.saveDatiAzienda(datiAzienda);
      setDatiAzienda({ ...DATI_AZIENDA_VUOTI, ...salvati });
      setAziendaSuccess('Dati azienda salvati');
    } catch (error: any) {
      setAziendaError(error.response?.data?.error || 'Errore durante il salvataggio dei dati azienda');
    } finally {
      setAziendaSaving(false);
    }
  };

  const handlePasswordChange = (field: keyof PasswordFormData, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    setPasswordErrors(prev => ({ ...prev, [field]: false, message: undefined }));
    setSuccessMessage(null);
  };

  const validatePasswordForm = (): boolean => {
    const errors: PasswordFormErrors = {};

    if (!passwordData.currentPassword.trim()) {
      errors.currentPassword = true;
    }
    if (!passwordData.newPassword.trim()) {
      errors.newPassword = true;
    } else if (passwordData.newPassword.length < 6) {
      errors.newPassword = true;
      errors.message = 'La nuova password deve avere almeno 6 caratteri';
    }
    if (!passwordData.confirmPassword.trim()) {
      errors.confirmPassword = true;
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = true;
      errors.message = 'Le password non coincidono';
    }

    setPasswordErrors(errors);
    return Object.keys(errors).filter(k => k !== 'message').length === 0;
  };

  const handleSubmitPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePasswordForm()) {
      return;
    }

    setIsSubmitting(true);
    setSuccessMessage(null);

    try {
      await api.put('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword
      });

      setSuccessMessage('Password modificata con successo');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Errore durante il cambio password';
      setPasswordErrors({ message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>
          <Settings size={28} />
          Impostazioni
        </h1>
      </div>

      <div className={styles.sectionsGrid}>
        {/* Account Info Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <User size={20} />
            <h2 className={styles.sectionTitle}>Informazioni Account</h2>
          </div>
          <div className={styles.sectionBody}>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Nome</span>
                <span className={styles.infoValue}>{user?.nome || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Cognome</span>
                <span className={styles.infoValue}>{user?.cognome || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Email</span>
                <span className={styles.infoValue}>{user?.email || '-'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Ruolo</span>
                <span className={styles.infoValue}>
                  <span className={`${styles.badge} ${user?.ruolo === 'admin' ? styles.badgeAdmin : styles.badgeUser}`}>
                    <Shield size={12} />
                    {user?.ruolo === 'admin' ? 'Amministratore' : 'Utente'}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Dati azienda: intestazione dei documenti d'ordine */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <Building2 size={20} />
            <h2 className={styles.sectionTitle}>Dati Azienda</h2>
          </div>
          <div className={styles.sectionBody}>
            {aziendaLoading ? (
              <p>Caricamento…</p>
            ) : (
              <form onSubmit={handleSubmitAzienda}>
                <p className={styles.infoLabel} style={{ marginBottom: 16 }}>
                  Questi dati compaiono come intestazione sui documenti d'ordine stampati
                  da allegare alle spedizioni.
                </p>

                {aziendaSuccess && (
                  <div className={styles.successAlert}>
                    <Check size={18} />
                    {aziendaSuccess}
                  </div>
                )}

                {aziendaError && (
                  <div className={styles.errorAlert}>
                    <AlertTriangle size={18} />
                    {aziendaError}
                  </div>
                )}

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Ragione sociale *</label>
                  <input
                    type="text"
                    className={styles.formControl}
                    value={datiAzienda.ragioneSociale}
                    onChange={(e) => handleAziendaChange('ragioneSociale', e.target.value)}
                    placeholder="Es. Rossi Srl"
                    maxLength={150}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Indirizzo</label>
                  <input
                    type="text"
                    className={styles.formControl}
                    value={datiAzienda.indirizzo || ''}
                    onChange={(e) => handleAziendaChange('indirizzo', e.target.value)}
                    placeholder="Via e numero civico"
                    maxLength={200}
                  />
                </div>

                <div className={styles.infoGrid}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>CAP</label>
                    <input
                      type="text"
                      className={styles.formControl}
                      value={datiAzienda.cap || ''}
                      onChange={(e) => handleAziendaChange('cap', e.target.value)}
                      maxLength={10}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Città</label>
                    <input
                      type="text"
                      className={styles.formControl}
                      value={datiAzienda.citta || ''}
                      onChange={(e) => handleAziendaChange('citta', e.target.value)}
                      maxLength={100}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Provincia</label>
                    <input
                      type="text"
                      className={styles.formControl}
                      value={datiAzienda.provincia || ''}
                      onChange={(e) => handleAziendaChange('provincia', e.target.value.toUpperCase())}
                      placeholder="MI"
                      maxLength={5}
                    />
                  </div>
                </div>

                <div className={styles.infoGrid}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Partita IVA</label>
                    <input
                      type="text"
                      className={styles.formControl}
                      value={datiAzienda.partitaIva || ''}
                      onChange={(e) => handleAziendaChange('partitaIva', e.target.value)}
                      maxLength={20}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Codice fiscale</label>
                    <input
                      type="text"
                      className={styles.formControl}
                      value={datiAzienda.codiceFiscale || ''}
                      onChange={(e) => handleAziendaChange('codiceFiscale', e.target.value.toUpperCase())}
                      maxLength={20}
                    />
                  </div>
                </div>

                <div className={styles.infoGrid}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Telefono</label>
                    <input
                      type="text"
                      className={styles.formControl}
                      value={datiAzienda.telefono || ''}
                      onChange={(e) => handleAziendaChange('telefono', e.target.value)}
                      maxLength={30}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Email</label>
                    <input
                      type="email"
                      className={styles.formControl}
                      value={datiAzienda.email || ''}
                      onChange={(e) => handleAziendaChange('email', e.target.value)}
                      maxLength={120}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Sito web</label>
                    <input
                      type="text"
                      className={styles.formControl}
                      value={datiAzienda.sitoWeb || ''}
                      onChange={(e) => handleAziendaChange('sitoWeb', e.target.value)}
                      maxLength={120}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Note in fondo al documento</label>
                  <textarea
                    className={styles.formControl}
                    value={datiAzienda.noteDocumento || ''}
                    onChange={(e) => handleAziendaChange('noteDocumento', e.target.value)}
                    placeholder="Es. condizioni di consegna, riferimenti per resi…"
                    rows={3}
                  />
                </div>

                <div className={styles.formActions}>
                  <button type="submit" className={styles.btnPrimary} disabled={aziendaSaving}>
                    {aziendaSaving ? 'Salvataggio...' : 'Salva Dati Azienda'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>

        {/* Password Change Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <Key size={20} />
            <h2 className={styles.sectionTitle}>Cambio Password</h2>
          </div>
          <div className={styles.sectionBody}>
            <form onSubmit={handleSubmitPassword}>
              {/* Success Message */}
              {successMessage && (
                <div className={styles.successAlert}>
                  <Check size={18} />
                  {successMessage}
                </div>
              )}

              {/* Error Message */}
              {passwordErrors.message && (
                <div className={styles.errorAlert}>
                  <AlertTriangle size={18} />
                  {passwordErrors.message}
                </div>
              )}

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Password attuale *</label>
                <div className={styles.passwordInputWrapper}>
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    className={`${styles.formControl} ${passwordErrors.currentPassword ? styles.error : ''}`}
                    value={passwordData.currentPassword}
                    onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                    placeholder="Inserisci la password attuale"
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {passwordErrors.currentPassword && (
                  <span className={styles.errorText}>Campo obbligatorio</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Nuova password *</label>
                <div className={styles.passwordInputWrapper}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    className={`${styles.formControl} ${passwordErrors.newPassword ? styles.error : ''}`}
                    value={passwordData.newPassword}
                    onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                    placeholder="Inserisci la nuova password"
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {passwordErrors.newPassword && (
                  <span className={styles.errorText}>
                    {passwordData.newPassword.length > 0 && passwordData.newPassword.length < 6
                      ? 'Minimo 6 caratteri'
                      : 'Campo obbligatorio'}
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Conferma nuova password *</label>
                <div className={styles.passwordInputWrapper}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className={`${styles.formControl} ${passwordErrors.confirmPassword ? styles.error : ''}`}
                    value={passwordData.confirmPassword}
                    onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                    placeholder="Conferma la nuova password"
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {passwordErrors.confirmPassword && (
                  <span className={styles.errorText}>
                    {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword
                      ? 'Le password non coincidono'
                      : 'Campo obbligatorio'}
                  </span>
                )}
              </div>

              <div className={styles.formActions}>
                <button
                  type="submit"
                  className={styles.btnPrimary}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Salvataggio...' : 'Cambia Password'}
                </button>
              </div>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ImpostazioniPage;
